import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Popover from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import styles from "./index.module.scss";

export interface LayoutSettingsItem {
  key: string;
  label: string;
  visible: boolean;
  /** true ise kullanıcı gizleyemez. */
  locked?: boolean;
}

export interface LayoutSettingsProps {
  /** Tetikleyici düğme metni (ör. "Kolonlar", "Düzeni özelleştir"). */
  buttonLabel: string;
  panelTitle: string;
  /** "{{visible}} / {{total}} ... görünür" biçiminde açıklama. */
  hint: string;
  items: LayoutSettingsItem[];
  isSaving?: boolean;
  isCustomized?: boolean;
  onToggle: (key: string) => void;
  onMove: (key: string, direction: "up" | "down") => void;
  onReset: () => void;
}

/**
 * Sıralanabilir + gizlenebilir öğe listesi paneli.
 *
 * Hem rapor tablolarının kolon ayarları hem de gösterge panelinin widget düzeni
 * için kullanılır (R11); iki yerde de "hangi öğeler, hangi sırada" sorusu aynı.
 */
const LayoutSettings: React.FC<LayoutSettingsProps> = ({
  buttonLabel,
  panelTitle,
  hint,
  items,
  isSaving = false,
  isCustomized = false,
  onToggle,
  onMove,
  onReset,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const visibleCount = items.filter((i) => i.visible).length;

  const content = (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.title}>{panelTitle}</span>
        <button type="button" className={styles.resetButton} onClick={onReset} disabled={isSaving}>
          {t("reportSettings.reset")}
        </button>
      </div>

      <p className={styles.hint}>{hint}</p>

      <ul className={styles.list}>
        {items.map((item, index) => (
          <li key={item.key} className={styles.item}>
            {/*
              Ortak Checkbox bileşeni: kutu, tik ve label tıklaması bir arada.
              (Tik artık `ui/checkbox/index.scss`'teki global kuralda çizildiği
              için ham input da doğru görünür.)
            */}
            <div className={styles.checkboxLabel}>
              <Checkbox
                value={item.visible}
                label={item.label}
                // Kilitli öğeler ve son görünür öğe kapatılamaz.
                disabled={item.locked || (item.visible && visibleCount === 1)}
                onChange={() => onToggle(item.key)}
              />
            </div>

            <span className={styles.moveButtons}>
              <button
                type="button"
                aria-label={t("reportSettings.moveUp")}
                title={t("reportSettings.moveUp")}
                onClick={() => onMove(item.key, "up")}
                disabled={index === 0 || isSaving}
              >
                ↑
              </button>
              <button
                type="button"
                aria-label={t("reportSettings.moveDown")}
                title={t("reportSettings.moveDown")}
                onClick={() => onMove(item.key, "down")}
                disabled={index === items.length - 1 || isSaving}
              >
                ↓
              </button>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <Popover content={content} isOpen={isOpen} onClose={() => setIsOpen(false)} position="bottom">
      <button
        type="button"
        className={`${styles.trigger} ${isCustomized ? styles.triggerCustomized : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {buttonLabel}
        {isCustomized && <span className={styles.dot} aria-hidden />}
      </button>
    </Popover>
  );
};

export default LayoutSettings;
